import PropTypes from "prop-types"
import React, { useMemo, useState, useEffect } from "react"
import swal from 'sweetalert'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  Button,
  UncontrolledTooltip,
  Badge
} from "reactstrap"
import { Link } from "react-router-dom"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import TableContainer from "../../components/Common/TableContainer"
import { getProperties, createProp, delPropById } from "../../helpers/requests" // Import the getProperties and createProp functions

import { Oval } from "react-loader-spinner";

const Property = (props) => {
  useEffect(() => {
    document.title = "Propriété"
  }, [])

  const [properties, setProperties] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState(() => localStorage.getItem("prop_filter") || "all");
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("prop_search") || "");

  useEffect(() => {
    localStorage.setItem("prop_filter", filter);
    localStorage.setItem("prop_search", searchTerm);
  }, [filter, searchTerm]);

  // Fetch properties from the backend when the component loads
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const data = await getProperties()
        setProperties(Array.isArray(data) ? data : [])
        setIsLoading(false)

        // Sync to hotel manager in background (non-blocking)
        for (const property of data) {
          createProp(property.id).catch(() => {})
        }
      } catch (error) {
        console.error("Error fetching properties:", error)
        setIsLoading(false)
      }
    }

    fetchProperties()
  }, [])


  const filteredProperties = useMemo(() => {
    let filtered = properties;

    if (filter !== "all") {
      filtered = filtered.filter((property) => property.type === filter);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (property) =>
          property.owner.toLowerCase().includes(searchTerm.toLowerCase()) ||
          property.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [properties, filter, searchTerm]);


  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedRows([])
    } else {
      setSelectedRows(properties.map(property => property.id))
    }
    setIsSelectAllChecked(!isSelectAllChecked)
  }

  const handleRowSelect = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter(rowId => rowId !== id))
    } else {
      setSelectedRows([...selectedRows, id])
    }
  }

  const handleDelete = (propertyData) => {
    swal({
      title: "Êtes-vous sûr?",
      text: "Une fois supprimé, vous ne pourrez pas récupérer cette propriété",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    })
    .then((willDelete) => {
      if (willDelete) {
        setProperties(properties.filter(property => property.id !== propertyData.id))
        swal("Propriété supprimée!", {
          icon: "success",
        })
      } else {
        swal("Suppression annulée")
      }
    })
  }

  const handleEdit = (propertyData) => {
    swal({
      title: "Modifier la Propriété",
      content: {
        element: "div",
        attributes: {
          innerHTML: `
            <div>
              <label for="owner">Propriétaire:</label>
              <input type="text" id="owner" value="${propertyData.owner}" class="swal-content__input" />
              <label for="code">Code de Propriété:</label>
              <input type="text" id="code" value="${propertyData.code}" class="swal-content__input" />
              <label for="propertyType">Type de Propriété:</label>
              <select id="propertyType" class="swal-content__input">
                <option value="HOTEL" ${propertyData.type === "HOTEL" ? "selected" : ""}>Hôtel</option>
                <option value="DORMITORY" ${propertyData.type === "DORMITORY" ? "selected" : ""}>Dortoire</option>
                <option value="HOUSE" ${propertyData.type === "HOUSE" ? "selected" : ""}>Maison</option>
              </select>
            </div>
          `
        }
      },
      buttons: true,
    })
    .then((willSave) => {
      if (willSave) {
        const owner = document.getElementById("owner").value
        const code = document.getElementById("code").value
        const type = document.getElementById("propertyType").value

        const updatedProperties = properties.map(property =>
          property.id === propertyData.id
            ? { ...property, owner, code, type }
            : property
        )

        setProperties(updatedProperties)
        swal("Propriété mise à jour!", {
          icon: "success",
        })
      }
    })
  }

  const columns = useMemo(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
        cell: (cellProps) => <span>{cellProps.row.original.id}</span>,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Propriétaire',
        accessorKey: 'owner',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Code de Propriété',
        accessorKey: 'code',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Type de Propriété',
        accessorKey: 'type',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Statut',
        accessorKey: 'available',
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const isAvailable = cellProps.row.original.available;
          return (
            <Badge color={isAvailable ? "success" : "danger"}>
              {isAvailable ? "Running" : "Stopped"}
            </Badge>
          );
        },
      },
      {
        header: 'Action',
        accessorKey: 'action',
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => (
          <div className="d-flex gap-3">

            <Link
              to="#"
              className="text-primary"
              onClick={() => {
                const propertyData = cellProps.row.original;
                // Navigate to property detail page
                window.location.href = `/properties/${propertyData.id}`;
              }}
            >
              <i className="mdi mdi-eye font-size-18" id="edittooltip" />
              <UncontrolledTooltip placement="top" target="edittooltip">
                Check
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={async() => {
                const propertyData = cellProps.row.original;
                // Navigate to property detail page
                await delPropById(cellProps.row.original.id)
                const data = await getProperties()
                setProperties(data)
              }}
            >
              <i className="mdi mdi-delete font-size-18" id="deletetooltip" />
              <UncontrolledTooltip placement="top" target="deletetooltip">
                delete
              </UncontrolledTooltip>
            </Link>
          </div>
        ),
      },
    ],
    [selectedRows, isSelectAllChecked, properties]
  );
  

  return (
    <React.Fragment>
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title={props.t("Propriété")} breadcrumbItem={props.t("Propriété")} />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <CardTitle style={{ marginBottom: "40px" }}>{props.t("Liste des Propriétés")}</CardTitle>

                <Row className="mb-3">
                  <Col md="4">
                    <FormGroup>
                      <Label for="filterSelect">Filter by Type</Label>
                      <Input
                        type="select"
                        id="filterSelect"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="HOTEL">Hôtel</option>
                        <option value="DORMITORY">Dortoire</option>
                        <option value="HOUSE">Maison</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="searchInput">Search</Label>
                      <Input
                        type="text"
                        id="searchInput"
                        placeholder="Search by owner or code"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4" className="d-flex align-items-end justify-content-center mb-2">
                    <Button
                      color="success"
                      className="btn-rounded waves-effect waves-light mb-2"
                      onClick={() => { window.location.href = 'proprety/create' }}
                    >
                      Ajoute une Propriété
                    </Button>
                  </Col>
                </Row>

                {isLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                    <Oval height={80} width={80} color="#556ee6" visible={true} ariaLabel="oval-loading" secondaryColor="#4fa94d" strokeWidth={2} strokeWidthSecondary={2} />
                  </div>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={filteredProperties}
                    isGlobalFilter={false}
                    isAddButton={false}
                    isPagination={true}
                    SearchPlaceholder="Cherchez une propriété"
                    
                    
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                    theadClass="table-light"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                   
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  </React.Fragment>
  )
}

Property.propTypes = {
  t: PropTypes.func.isRequired,
}

export default withTranslation()(Property)
